/** Brand kit Video (Aziz 2026-10-29, docs/BRIEF-VIDEO-OUTRO.md): the clinic's outros and music list,
 *  and renders of a clip + the chosen sound + the clinic's outro. */
import { api } from './api';

export const SHAPES = ['vertical', 'square', 'wide'] as const;
export type Shape = (typeof SHAPES)[number];
export const SHAPE_LABEL: Record<Shape, string> = { vertical: 'Vertical 9:16', square: 'Square 1:1', wide: 'Wide 16:9' };
export const AUDIO_MODES = [
  { value: 'original', label: "Video's own sound", hint: 'Keep the clip as it sounds.' },
  { value: 'music', label: 'Music only', hint: "The clip's sound is replaced by the track." },
  { value: 'voice_up', label: 'Both, voice first', hint: 'The clip stays clear; the music plays softly under it.' },
  { value: 'music_up', label: 'Both, music first', hint: "The music leads; the clip's sound plays quietly under it." },
] as const;
export type AudioMode = (typeof AUDIO_MODES)[number]['value'];
export const VIDEO_ACCEPT = 'video/mp4,video/quicktime,video/webm,video/x-m4v';
export const AUDIO_ACCEPT = 'audio/mpeg,audio/mp4,audio/x-m4a,audio/aac,audio/wav,audio/x-wav,.mp3,.m4a,.wav,.aac';

export interface Track { id: string; name: string; path: string }
export interface VideoSettings { outros: Record<Shape, string>; music: Track[]; can_manage: boolean }
export interface Render {
  id: string; status: 'queued' | 'running' | 'done' | 'failed'; clip_name: string; shape: string; audio: AudioMode;
  music_name: string | null; error: string | null; duration_s: number | null; created_at: string; finished_at: string | null;
  download_url: string | null;
}

const base = (clientId: string) => `/clients/${clientId}/brand-kit/video`;
export const fetchVideoSettings = async (clientId: string): Promise<VideoSettings> => (await api.get(base(clientId))).data;
export const saveVideoSettings = async (clientId: string, s: Omit<VideoSettings, 'can_manage'>): Promise<VideoSettings> =>
  (await api.put(base(clientId), s)).data;
export const previewUrl = async (clientId: string, path: string): Promise<string> =>
  (await api.get(`${base(clientId)}/preview`, { params: { path } })).data.url;
export const fetchRenders = async (clientId: string): Promise<Render[]> => (await api.get(`${base(clientId)}/renders`)).data;
export const fetchRender = async (clientId: string, id: string): Promise<Render> => (await api.get(`${base(clientId)}/renders/${id}`)).data;
export const createRender = async (clientId: string, body: {
  clip_path: string; clip_name: string; shape: 'auto' | Shape; audio: AudioMode; music_id?: string | null; track_path?: string | null;
}): Promise<Render> => (await api.post(`${base(clientId)}/renders`, body)).data;

/** A browser may give no type for a .mov or .m4a; fill it from the extension so storage accepts it. */
function typeOf(f: File): string {
  if (f.type) return f.type === 'audio/x-m4a' ? 'audio/mp4' : f.type;
  const ext = f.name.split('.').pop()?.toLowerCase();
  return ({ mov: 'video/quicktime', mp4: 'video/mp4', m4v: 'video/x-m4v', webm: 'video/webm', mp3: 'audio/mpeg',
    m4a: 'audio/mp4', wav: 'audio/wav', aac: 'audio/aac' } as Record<string, string>)[ext ?? ''] ?? '';
}

/** Upload one file straight to storage with progress (0..1); returns its path. */
export async function uploadVideoFile(clientId: string, slot: string, file: File, onProgress?: (p: number) => void): Promise<string> {
  const r = (await api.post(`${base(clientId)}/upload-url`, { slot, content_type: typeOf(file), filename: file.name })).data as
    { upload_url: string; path: string; content_type: string };
  await new Promise<void>((resolve, reject) => {
    const x = new XMLHttpRequest();
    x.open('PUT', r.upload_url);
    x.setRequestHeader('Content-Type', r.content_type);
    x.upload.onprogress = (e) => { if (e.lengthComputable) onProgress?.(e.loaded / e.total); };
    x.onload = () => (x.status >= 200 && x.status < 300 ? resolve() : reject(new Error(`Storage refused the file (${x.status})`)));
    x.onerror = () => reject(new Error('The upload was interrupted. Check the connection and try again.'));
    x.send(file);
  });
  return r.path;
}
